import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Badge, InputGroup, Modal } from 'react-bootstrap';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaEye, FaQrcode } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const GymMembers = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [trainers, setTrainers] = useState([]);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', dateOfBirth: '', gender: 'male',
    address: '', emergencyContact: '', bloodGroup: '', trainerId: ''
  });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchMembers();
    fetchTrainers();
  }, [search, statusFilter]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/gym/members', {
        params: { search, status: statusFilter }
      });
      setMembers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const res = await axios.get('/api/gym/trainers');
      setTrainers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`/api/gym/members/${editId}`, formData);
      } else {
        await axios.post('/api/gym/members', formData);
      }
      setShowModal(false);
      setFormData({ name: '', phone: '', email: '', dateOfBirth: '', gender: 'male', address: '', emergencyContact: '', bloodGroup: '', trainerId: '' });
      setEditId(null);
      fetchMembers();
    } catch (err) {
      console.error(err);
      alert('Error saving member');
    }
  };

  const handleEdit = (member) => {
    setFormData(member);
    setEditId(member.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this member?')) {
      try {
        await axios.delete(`/api/gym/members/${id}`);
        fetchMembers();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <Badge bg="secondary">INACTIVE</Badge>;
    
    const variants = {
      active: 'success',
      expired: 'danger',
      frozen: 'warning',
      inactive: 'secondary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-0">💪 Gym Members</h2>
          <p className="text-muted">Manage your gym members</p>
        </Col>
        <Col xs="auto">
          <Button variant="info" size="lg" className="me-2" onClick={() => navigate('/gym/qr-scanner')}>
            <FaQrcode className="me-2" /> Scan QR
          </Button>
          <Button variant="success" size="lg" onClick={() => { setShowModal(true); setEditId(null); setFormData({ name: '', phone: '', email: '', dateOfBirth: '', gender: 'male', address: '', emergencyContact: '', bloodGroup: '', trainerId: '' }); }}>
            <FaPlus className="me-2" /> Add Member
          </Button>
        </Col>
      </Row>

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, phone, or member ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="frozen">Frozen</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="text-muted">No members found</h5>
              <p>Start by adding your first gym member</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Member ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Join Date</th>
                  <th>Status</th>
                  <th>Trainer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id}>
                    <td><strong>{member.memberId}</strong></td>
                    <td>{member.name}</td>
                    <td>{member.phone}</td>
                    <td>{new Date(member.joinDate).toLocaleDateString()}</td>
                    <td>{getStatusBadge(member.status)}</td>
                    <td>{member.trainerName || 'Not Assigned'}</td>
                    <td>
                      <Button variant="outline-info" size="sm" className="me-1" onClick={() => navigate(`/gym/members/${member.id}`)}>
                        <FaEye />
                      </Button>
                      <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEdit(member)}>
                        <FaEdit />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(member.id)}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editId ? 'Edit' : 'Add'} Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone *</Form.Label>
                  <Form.Control required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date of Birth</Form.Label>
                  <Form.Control type="date" value={formData.dateOfBirth} onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gender</Form.Label>
                  <Form.Select value={formData.gender} onChange={(e) => setFormData({ ...formData, gender: e.target.value })}>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Blood Group</Form.Label>
                  <Form.Control value={formData.bloodGroup} onChange={(e) => setFormData({ ...formData, bloodGroup: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control as="textarea" rows={2} value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Emergency Contact</Form.Label>
                  <Form.Control value={formData.emergencyContact} onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Assign Trainer</Form.Label>
                  <Form.Select value={formData.trainerId} onChange={(e) => setFormData({ ...formData, trainerId: e.target.value })}>
                    <option value="">No Trainer</option>
                    {trainers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Button type="submit" variant="success">Save Member</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default GymMembers;
